import React, { useState } from 'react';
import { getStats } from './stats';
import {
  pushSupported,
  isPushEnabled,
  notificationPermission,
  enablePushReminders,
  disablePushReminders,
  REMINDER_HOUR,
} from './push';
import TopNav from './TopNav';

function ReminderToggle() {
  const [enabled, setEnabled] = useState(isPushEnabled());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  if (!pushSupported()) {
    return (
      <p className='reminder-note'>
        Streak reminders aren’t supported on this browser. On iPhone, add this app to your
        Home Screen first.
      </p>
    );
  }

  const blocked = notificationPermission() === 'denied';

  const toggle = async () => {
    setBusy(true);
    setError('');
    try {
      if (enabled) {
        await disablePushReminders();
        setEnabled(false);
      } else {
        const ok = await enablePushReminders();
        if (ok) setEnabled(true);
        else setError('Notification permission was not granted.');
      }
    } catch (e) {
      setError(e.message || 'Something went wrong.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className='reminder-toggle'>
      <button
        type='button'
        className={`reminder-btn ${enabled ? 'on' : ''}`}
        onClick={toggle}
        disabled={busy || blocked}
      >
        <span className='reminder-dot' aria-hidden='true' />
        {busy ? 'Working…' : enabled ? 'Streak reminders on' : 'Remind me before I lose my streak'}
      </button>
      <p className='reminder-note'>
        {blocked
          ? 'Notifications are blocked in your browser settings — enable them to get reminders.'
          : `A quiet nudge around ${REMINDER_HOUR}:00 your time, only when a streak is at risk.`}
      </p>
      {error && <p className='admin-error'>{error}</p>}
    </div>
  );
}

function StatsPage() {
  const stats = getStats();
  const maxCount = Math.max(1, ...stats.days.map((d) => d.count));

  return (
    <div className='screen'>
      <TopNav active='progress' />

      <main className='stats-paper'>
        <div className='stats-kicker'>Your progress</div>
        <h1 className='stats-title'>The Almanac</h1>
        <div className='rule-double' aria-hidden='true' />

        <section className={`streak-hero ${stats.streak > 0 ? 'active' : ''}`}>
          <div className='streak-figure'>
            <span className='streak-flame' aria-hidden='true'>
              <svg viewBox='0 0 24 24' fill='currentColor'>
                <path d='M12.5 1.5c.7 2.7-.9 4.1-2.1 5.7-1.1 1.4-1.9 2.9-.7 4.6.4-.9.7-1.8 1.7-2.4.6 2-.9 3.1.2 4.8.6-.4 1-1 1.1-1.8 1.4 1 2.4 2.6 2.4 4.4a5.6 5.6 0 1 1-11.2 0c0-3 1.7-4.8 3.1-6.7C8.6 7.9 9.6 5.3 8.6 2.4c1.3.4 3.1 1.5 3.9 3.2.3-1.4.3-2.7 0-4.1z' />
              </svg>
            </span>
            <span className='streak-value'>{stats.streak}</span>
          </div>
          <div className='streak-caption'>
            consecutive day{stats.streak === 1 ? '' : 's'}
          </div>
        </section>

        <section className='stat-row'>
          <div className='stat-figure'>
            <div className='stat-number'>{stats.today}</div>
            <div className='stat-label'>solved today</div>
          </div>
          <div className='stat-vrule' aria-hidden='true' />
          <div className='stat-figure'>
            <div className='stat-number'>{stats.total}</div>
            <div className='stat-label'>solved all-time</div>
          </div>
        </section>

        <section className='chart-block'>
          <div className='chart-head'>
            <span>Last seven days</span>
            <span className='chart-peak'>peak {maxCount}</span>
          </div>
          <div className='stat-chart'>
            {stats.days.map((d, i) => {
              const isToday = i === stats.days.length - 1;
              return (
                <div
                  className={`stat-bar-col ${isToday ? 'today' : ''} ${d.count === 0 ? 'empty' : ''}`}
                  key={d.key}
                >
                  <div className='stat-bar-count'>{d.count > 0 ? d.count : ''}</div>
                  <div className='stat-bar-track'>
                    <div
                      className='stat-bar-fill'
                      style={{ height: `${(d.count / maxCount) * 100}%`, animationDelay: `${i * 60}ms` }}
                    />
                  </div>
                  <div className='stat-bar-label'>{isToday ? 'Today' : d.label}</div>
                </div>
              );
            })}
          </div>
        </section>

        <ReminderToggle />
      </main>
    </div>
  );
}

export default StatsPage;
